import { PRESCRIPTION_ACCEPTED_FILE_TYPES } from "@akina/domain";
import type { SharedFormatMessage } from "@akina/infra";
import { formatBytes } from "@akina/utils";
import { clsx } from "clsx";
import { BASE_FILE_SIZE_LIMIT_BYTES, Icon } from "../atoms";
import { FileDropZone } from "../molecules";

const PARSED_ACCEPTED_FILE_TYPES = PRESCRIPTION_ACCEPTED_FILE_TYPES.map(
  type => ` .${(type as string).split("/")[1].toUpperCase()}`,
);

type UploadPrescriptionDropzoneProps = {
  formatMessage: SharedFormatMessage;
  files: File[];
  setFiles: (file: File[]) => void;
  setIsDropActive: (isActive: boolean) => void;
  hadError?: boolean;
  setHadError?: (hadError: boolean) => void;
  maxFileSizeBytes?: number;
  disabled?: boolean;
  isDropActive?: boolean;
};

export function UploadPrescriptionDropzone(props: UploadPrescriptionDropzoneProps) {
  const {
    formatMessage,
    files,
    setFiles,
    setIsDropActive,
    hadError,
    setHadError,
    maxFileSizeBytes = BASE_FILE_SIZE_LIMIT_BYTES,
    disabled = false,
    isDropActive = false,
  } = props;

  const containerClassName = clsx(
    "flex items-center justify-between gap-4 rounded-2xl p-4 transition-all",
    disabled && "pointer-events-none border border-dashed border-gray-800/10",
    !disabled && files.length > 0 && "bg-gray-800/5",
    !disabled && files.length === 0 && hadError && "border border-dashed border-gray-800/20",
    !disabled && files.length === 0 && !hadError && "border border-dashed border-gray-800/50",
    !disabled && isDropActive && files.length === 0 && "bg-gray-200",
  );

  const showIcon = files.length === 0 || disabled;
  const iconType: "uploadPrescription" | "prescriptionUploadFailed" | undefined = showIcon
    ? hadError && !disabled
      ? "prescriptionUploadFailed"
      : "uploadPrescription"
    : undefined;
  const iconClassName = disabled ? "h-full w-full opacity-60" : "h-full w-full";

  const notifyError = () => {
    setHadError?.(true);
  };

  const dropzone = (
    <FileDropZone
      showIcon={false}
      hadError={hadError}
      setIsDropActive={setIsDropActive}
      supportedFileTypesText={
        formatMessage({ id: "shared.prescription.dropzone.supportedFileTypes" }) + PARSED_ACCEPTED_FILE_TYPES.join(",")
      }
      maxFileSizeText={
        formatMessage({ id: "shared.prescription.dropzone.maxFileSize" }) + " " + formatBytes(maxFileSizeBytes)
      }
      acceptedFileTypes={PRESCRIPTION_ACCEPTED_FILE_TYPES}
      fileSizeLimitBytes={maxFileSizeBytes}
      browseButtonText={formatMessage({ id: "shared.prescription.dropzone.browse" })}
      onFilesChange={setFiles}
      onFileSizeLimitExceeded={notifyError}
      onFileTypeError={notifyError}
      files={files}
      successText={formatMessage({ id: "shared.prescription.dropzone.successText" })}
      tryAgainText={formatMessage({ id: "shared.prescription.dropzone.tryAgainText" })}
      errorText={formatMessage({ id: "shared.prescription.dropzone.errorText" })}
      disabled={disabled}>
      {formatMessage({ id: "shared.prescription.dropzone.dropPrescription" })}
    </FileDropZone>
  );

  return (
    <div className={containerClassName} aria-hidden={disabled}>
      {iconType ? (
        <div className="grid-cols-uploadPrescription grid items-center gap-4">
          <Icon type={iconType} className={iconClassName} />
          {dropzone}
        </div>
      ) : (
        dropzone
      )}
    </div>
  );
}
